// ===========================================
// ACCOUNT INSIGHTS — pure computations behind the Accounts extras
// ===========================================
// The "Needs you" worklist, the derived timeline events and the health flags
// are all plain functions over data the page already fetched (orders,
// invoices, deductions, CRM notes). Nothing here estimates: every line can
// point at the exact records that produced it, and when the data can't
// support a claim we say nothing rather than guess.

import { daysSince, fmtDay, netDaysFrom, timeAgo, deductionReason, WEEKDAY_NAMES } from '../../utils/cpg';
import { formatMoney } from '../../lib/money';

// The pipeline stages, shared with the page so board + insights never drift.
export const STAGES: Array<[string, string]> = [
  ['lead', 'Lead'], ['contacted', 'Contacted'], ['sample_sent', 'Sample sent'],
  ['negotiating', 'Negotiating'], ['won', 'Won'], ['lost', 'Lost'],
];
export const STAGE_LABEL: Record<string, string> = Object.fromEntries(STAGES);
export const ACTIVE_STAGES = ['lead', 'contacted', 'sample_sent', 'negotiating'];

export const isOverdue = (v?: string | null) => !!v && new Date(v).getTime() < new Date(new Date().toDateString()).getTime();
export const orderDate = (o: any) => o.fulfillmentDate || o.createdAt;

// Native-currency money (lib/money) — whole dollars for order totals,
// cents kept where small amounts matter (deductions).
const money = (cents: number, currency?: string, exact = false) => formatMoney(cents, currency || 'USD', { exact });

// ===========================================
// HEALTH FLAGS — named facts, not a composite score
// ===========================================
// Each flag is one plain-language claim with the numbers that back it. There
// is deliberately no 0–100 "health score": that would be precision we don't
// have. A flag either holds on the records or it isn't shown.
export type HealthFlag = {
  id: 'slow_payer' | 'overdue_reorder' | 'deduction_heavy' | 'growing' | 'declining';
  label: string;
  tip: string; // plain-language: what we saw and the threshold we used
  tone: 'red' | 'amber' | 'emerald';
};

export function healthFlags(a: any): HealthFlag[] {
  const st = a.stats;
  if (!st) return [];
  const flags: HealthFlag[] = [];

  // Slow payer: average days-to-pay beats their terms. Needs 2+ paid invoices
  // so one late check doesn't brand anyone.
  const terms = netDaysFrom(a.paymentTerms);
  if (st.avgDaysToPay != null && st.paidInvoiceCount >= 2 && st.avgDaysToPay > (terms ?? 30)) {
    flags.push({
      id: 'slow_payer', label: 'slow payer', tone: 'amber',
      tip: `Pays in ${st.avgDaysToPay} days on average across ${st.paidInvoiceCount} paid invoices — past ${terms != null ? `their ${a.paymentTerms} terms` : 'the 30 days we assume when no terms are set'}.`,
    });
  }

  // Overdue to reorder: past their own average gap (needs 3+ orders to exist).
  if (st.reorderOverdueDays != null && a.stage !== 'lost') {
    flags.push({
      id: 'overdue_reorder', label: 'due to reorder', tone: 'red',
      tip: `They order every ~${st.cadenceDays} days; the last order was ${timeAgo(st.lastOrderAt)} — ${st.reorderOverdueDays}d past their usual gap.`,
    });
  }

  // Deduction-heavy: deductions above 2% of their all-time revenue.
  if (st.revenueCents > 0 && st.deductionCents / st.revenueCents > 0.02) {
    const pct = ((st.deductionCents / st.revenueCents) * 100).toFixed(1);
    flags.push({
      id: 'deduction_heavy', label: 'deduction-heavy', tone: 'amber',
      tip: `${money(st.deductionCents, a.currency, true)} taken in deductions — ${pct}% of their revenue. We flag anything over 2%.`,
    });
  }

  // Direction: trailing 30 days vs the 30 before. ±20% before we call it a
  // trend — smaller moves are noise at this order count.
  if (st.momPct != null && st.momPct >= 20) {
    flags.push({
      id: 'growing', label: `growing +${st.momPct}%`, tone: 'emerald',
      tip: `Ordered ${st.momPct}% more in the last 30 days than the 30 before. Flagged at ±20%.`,
    });
  } else if (st.momPct != null && st.momPct <= -20) {
    flags.push({
      id: 'declining', label: `declining ${st.momPct}%`, tone: 'red',
      tip: `Ordered ${Math.abs(st.momPct)}% less in the last 30 days than the 30 before. Flagged at ±20%.`,
    });
  }

  return flags;
}

// ===========================================
// AUTO-EVENTS — what happened, woven into the timeline
// ===========================================
// Derived from orders, invoices, deductions and the standing order — rendered
// muted next to human notes. A CRM timeline is usually what someone typed;
// this is what the records say actually happened.
export type AutoEvent = {
  id: string;
  at: string;
  icon: 'order' | 'invoice' | 'deduction' | 'standing' | 'stage';
  text: string;
  sub?: string;
};

export function buildAutoEvents(a: any): AutoEvent[] {
  const st = a.stats;
  if (!st) return [];
  const evs: AutoEvent[] = [];

  for (const o of st.orders || []) {
    const at = o.createdAt || o.fulfillmentDate;
    if (!at) continue;
    const sub = [
      o.poNumber ? `PO ${o.poNumber}` : null,
      o.status === 'draft' ? 'draft — confirm in Wholesale' : null,
    ].filter(Boolean).join(' · ');
    evs.push({
      id: `order-${o.id}`, at, icon: 'order',
      text: `Order ${o.orderNumber} placed · ${money(o.totalCents, o.currencyCode)}`,
      sub: sub || undefined,
    });
  }

  for (const i of st.invoices || []) {
    if (i.status !== 'paid' || !i.paidAt) continue;
    const days = i.issuedAt ? Math.max(0, Math.round((new Date(i.paidAt).getTime() - new Date(i.issuedAt).getTime()) / 86400000)) : null;
    evs.push({
      id: `invpaid-${i.id}`, at: i.paidAt, icon: 'invoice',
      text: `Invoice ${i.invoiceNumber} paid · ${money(i.amountPaidCents || i.totalCents, i.currencyCode)}`,
      sub: days != null ? `${days} day${days === 1 ? '' : 's'} from issue to payment` : undefined,
    });
  }

  for (const d of st.deductions || []) {
    const at = d.deductionDate || d.createdAt;
    if (!at) continue;
    evs.push({
      id: `ded-${d.id}`, at, icon: 'deduction',
      text: `Deduction taken · ${money(d.amountCents, d.currency, true)} · ${deductionReason(d.reasonCode).toLowerCase()}`,
      sub: d.disputed ? 'disputed' : d.expected ? 'expected (budgeted trade spend)' : undefined,
    });
  }

  const sw = a.standingWeekly;
  if (sw?.setAt && sw.weekday != null) {
    evs.push({ id: `standing-${a.id}`, at: sw.setAt, icon: 'standing', text: `Standing weekly set · ${WEEKDAY_NAMES[sw.weekday]}s` });
  }

  if (a.stageSetAt && STAGE_LABEL[a.stage]) {
    evs.push({ id: `stage-${a.id}`, at: a.stageSetAt, icon: 'stage', text: `Moved to ${STAGE_LABEL[a.stage]}` });
  }

  return evs;
}

// ===========================================
// NEEDS YOU — the morning worklist
// ===========================================
// One prioritized queue across every account: commitments you made (next
// steps past due), money going stale (invoices newly crossing 30/60 days),
// money leaking (fresh deductions to review) and accounts past their own
// reorder rhythm. Ordered by urgency; each row jumps to its account.
export type NeedsYouItem = {
  key: string;
  accountId: string;
  accountName: string;
  kind: 'next_step' | 'invoice_60' | 'invoice_30' | 'reorder' | 'deduction';
  what: string; // what to do / what happened
  why: string;  // why it's on the list today — dates and numbers, no vibes
  urgency: number;
};

// How long a "newly crossed" invoice or a fresh deduction stays on the list.
const CROSSING_WINDOW_DAYS = 7;
const FRESH_DEDUCTION_DAYS = 14;

export function buildNeedsYou(rows: any[]): NeedsYouItem[] {
  const items: NeedsYouItem[] = [];

  for (const a of rows) {
    if (a.stage === 'lost') continue;
    const st = a.stats;
    if (!st) continue;

    // 1. Your own next step, past its due date. (The pipeline keeps the most
    // recent next step per account — that's the live commitment.)
    if (a.nextStep?.dueAt && isOverdue(a.nextStep.dueAt)) {
      const over = daysSince(a.nextStep.dueAt);
      items.push({
        key: `next-${a.id}`, accountId: a.id, accountName: a.name, kind: 'next_step',
        what: a.nextStep.note,
        why: `your next step · due ${fmtDay(a.nextStep.dueAt)} · ${over}d overdue`,
        urgency: 400 + Math.min(over, 90), // tier 2: your own overdue commitments
      });
    }

    // 2. Invoices that just crossed an aging bucket — newly 60+ outranks
    // everything, newly 30+ sits below overdue next steps. Only the crossing
    // week appears here; standing A/R lives on the account panel.
    for (const i of st.invoices || []) {
      if (i.status === 'paid') continue;
      const anchor = i.dueAt || i.issuedAt;
      if (!anchor) continue;
      const late = daysSince(anchor);
      const balance = (i.totalCents || 0) - (i.amountPaidCents || 0);
      if (balance <= 0) continue;
      if (late >= 60 && late < 60 + CROSSING_WINDOW_DAYS) {
        items.push({
          key: `inv60-${i.id}`, accountId: a.id, accountName: a.name, kind: 'invoice_60',
          what: `Invoice ${i.invoiceNumber} just crossed 60 days`,
          why: `${money(balance, i.currencyCode)} open · ${i.dueAt ? 'due' : 'issued'} ${fmtDay(anchor)} · now ${late}d`,
          urgency: 500 + (late - 60), // tier 1: money 60+ days stale
        });
      } else if (late >= 30 && late < 30 + CROSSING_WINDOW_DAYS) {
        items.push({
          key: `inv30-${i.id}`, accountId: a.id, accountName: a.name, kind: 'invoice_30',
          what: `Invoice ${i.invoiceNumber} just crossed 30 days`,
          why: `${money(balance, i.currencyCode)} open · ${i.dueAt ? 'due' : 'issued'} ${fmtDay(anchor)} · now ${late}d`,
          urgency: 300 + (late - 30), // tier 3: money 30+ days stale
        });
      }
    }

    // 3. Fresh deductions nobody has reviewed — not expected, not yet
    // disputed, taken in the last two weeks. Money leaks fastest when new.
    for (const d of st.deductions || []) {
      const at = d.deductionDate || d.createdAt;
      if (!at || d.expected || d.disputed) continue;
      const age = daysSince(at);
      if (age > FRESH_DEDUCTION_DAYS) continue;
      items.push({
        key: `ded-${d.id}`, accountId: a.id, accountName: a.name, kind: 'deduction',
        what: `Deduction taken · ${money(d.amountCents, d.currency, true)} · ${deductionReason(d.reasonCode).toLowerCase()}`,
        why: `taken ${timeAgo(at)} · review or dispute${d.invoiceNumber ? ` · ${d.invoiceNumber}` : ''}`,
        urgency: 200 + (FRESH_DEDUCTION_DAYS - age), // tier 4: fresher = act sooner
      });
    }

    // 4. Past their own reorder rhythm (needs 3+ orders for a rhythm to exist).
    if (st.reorderOverdueDays != null) {
      items.push({
        key: `reorder-${a.id}`, accountId: a.id, accountName: a.name, kind: 'reorder',
        what: `Due to reorder — usually every ~${st.cadenceDays}d`,
        why: `last order ${fmtDay(st.lastOrderAt)} · ${st.reorderOverdueDays}d past their gap · typical order ${money(st.aovCents, a.currency)}`,
        urgency: 100 + Math.min(st.reorderOverdueDays, 90), // tier 5: sales nudge
      });
    }
  }

  return items.sort((x, y) => y.urgency - x.urgency);
}
